import { useMemo, useState } from "react";
import { FilterTabItem } from "presentation/mobile/components/MobileFilterTabs";
import { ALL_STATUSES_KEY, StatusFilterOption } from "presentation/mobile/utils/status-filters.util";

// Statuses a tab covers: a grouped tab lists them, a simple tab is just its own key
const getOptionStatuses = (option: StatusFilterOption): string[] => option.statuses ?? [option.key];

// Devolve as abas ("Todos" + cada estado, com contadores), a lista filtrada e o estado ativo.
export function useStatusFilter<T>(
    items: T[],
    statusOptions: StatusFilterOption[],
    getStatus: (item: T) => string | undefined
) {
    const [activeTab, setActiveTab] = useState<string>(ALL_STATUSES_KEY);

    // Contagem por estado numa só passagem
    const countByStatus = useMemo(() => {
        const counts = new Map<string, number>();
        items.forEach((item) => {
            const status = getStatus(item) ?? "";
            counts.set(status, (counts.get(status) ?? 0) + 1);
        });
        return counts;
    }, [items, getStatus]);

    // "Todos" + todos os estados possíveis (sempre visíveis, mesmo com 0)
    const filterTabs: FilterTabItem<string>[] = useMemo(
        () => [
            { key: ALL_STATUSES_KEY, label: "Todos", count: items.length },
            ...statusOptions.map((option) => ({
                key: option.key,
                label: option.label,
                count: getOptionStatuses(option).reduce(
                    (total, status) => total + (countByStatus.get(status) ?? 0),
                    0
                )
            })),
        ],
        [statusOptions, countByStatus, items.length]
    );

    const filteredItems = useMemo(() => {
        if (activeTab === ALL_STATUSES_KEY) return items;

        const activeOption = statusOptions.find((option) => option.key === activeTab);
        if (!activeOption) return items;

        const activeStatuses = getOptionStatuses(activeOption);
        return items.filter((item) => activeStatuses.includes(getStatus(item) ?? ""));
    }, [items, activeTab, statusOptions, getStatus]);

    return { activeTab, setActiveTab, filterTabs, filteredItems };
}
